import React, {useEffect, useState} from 'react'
import {EventModel} from 'event/models/Event'
import {attendeeApiClient} from 'attendee/apiClient'
import {FullPageLoader} from 'attendee/theme/FullPageLoader'
import {useAuth} from 'attendee/Auth/Providers/AuthProvider'
import {useParamEventSlug} from 'event/EventRoutes'
import {toast} from 'react-toastify'
import {organizationApiClient} from 'organization/organizationApiClient'

export interface EventContextProps {
  event: EventModel | null
  loading: boolean
}

export const EventContext = React.createContext<EventContextProps | undefined>(
  undefined,
)

export default function EventProvider(props: {children: React.ReactElement}) {
  const apiClient = attendeeApiClient()
  const apiOrganizationClient = organizationApiClient()

  const [event, setEvent] = useState<EventModel | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const {user} = useAuth()
  const eventSlug = useParamEventSlug()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const get = async () => {
      try {
        const isPreview = params.get('preview') === '1'

        const response = isPreview
          ? await apiOrganizationClient.get(`api/preview/event/${eventSlug}`)
          : await apiClient.get(`api/event/${eventSlug}${user ? '/loged' : ''}`)
        setEvent(response)
        setLoading(false)
      } catch (error: any) {
        toast.warning(error.response.data.message)
        setLoading(false)
      }
    }

    get()
    // Intentional mount-only fetch; listing clients/slug/user retriggers unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [])

  return (
    <EventContext.Provider
      value={{
        event,
        loading,
      }}
    >
      {loading && <FullPageLoader />}
      {!loading && props.children}
    </EventContext.Provider>
  )
}

export function useEvent() {
  const context = React.useContext(EventContext)
  if (context === undefined) {
    throw new Error('useEvent must be used within EventProvider')
  }
  return context
}
